/**
 * Local Print Bridge client — talks to the small Python helper running
 * on the cashier's POS computer (see /app/frontend/public/downloads/).
 *
 * The bridge listens on http://localhost:9101 and proxies print jobs
 * to network thermal printers (IP:9100) so the browser doesn't have to
 * show a print dialog. Modern Chrome/Edge/Safari treat http://localhost
 * as a secure context (no mixed-content block) from https origins, so
 * this works straight from the deployed POS site.
 *
 * iter644 — Port fix: bridge always listens on 9101 (see smarto_print_bridge.py
 * line 73: `BRIDGE_PORT = int(os.environ.get("BRIDGE_PORT", "9101"))`).
 * The previous value (8765) never matched the running bridge, so the
 * "Silent print not active on this PC" banner stayed yellow forever.
 * iter646 — Env override (`REACT_APP_PRINT_BRIDGE_URL`) lets ops point
 * the browser at a non-default port / host without a code redeploy.
 */

const BRIDGE_BASE = (process.env.REACT_APP_PRINT_BRIDGE_URL || 'http://localhost:9101').replace(/\/+$/, '');
const FETCH_TIMEOUT_MS = 4000;

async function _fetch(path, init = {}) {
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), FETCH_TIMEOUT_MS);
  try {
    const r = await fetch(`${BRIDGE_BASE}${path}`, { ...init, signal: ctl.signal });
    const json = await r.json().catch(() => ({}));
    return { ok: r.ok && json.ok !== false, status: r.status, data: json };
  } catch (e) {
    return { ok: false, status: 0, data: { error: String(e?.message || e) } };
  } finally {
    clearTimeout(t);
  }
}

export async function isBridgeAlive() {
  const r = await _fetch('/health');
  return r.ok;
}

export async function pingPrinter(ip, port = 9100) {
  return _fetch('/ping', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ip, port }),
  });
}

/** iter653 — Ask the local Print Bridge to scan the LAN for thermal
 *  printers (TCP-9100 sweep + mDNS / Bonjour if `zeroconf` is installed
 *  on the bridge). Returns `{ ok, data: { printers: [...] } }`.
 *  Each printer entry exposes at least { ip, port, name?, source }.
 *  Times out via the shared 4s AbortController. */
export async function discoverPrinters() {
  return _fetch('/discover');
}

/**
 * iter660 — Ask the local Print Bridge to probe a single printer's web
 * admin (port 80 by default) and parse out its model from the HTML
 * <title>. Returns `{ ok, data: { ok, identity, model_hint, raw_status }}`.
 * Requires Bridge v1.4.0+. On older bridges the route 404s and we surface
 * that as `{ ok: false, status: 404 }`.
 */
export async function identifyPrinter(ip, port = 80) {
  return _fetch('/identify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ip, port }),
  });
}

/**
 * iter662 — Verify a printer's AirPrint state via the local Bridge.
 * Combines: (a) HTTP scrape of the printer's WebConfig admin pages, and
 * (b) Bonjour browse of `_ipp._tcp.local.` (when zeroconf is installed).
 * Returns `{ ok, data: { ok, ip, airprint_admin_state, bonjour_advertising,
 * hostname, details } }`. Requires Bridge v1.5.0+.
 */
export async function verifyAirPrint(ip, port = 80) {
  return _fetch('/verify-airprint', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ip, port }),
  });
}

/**
 * iter663 — Programmatically enable AirPrint on the printer + reboot.
 * Best-effort: the Bridge tries every known Epson WebConfig endpoint
 * and reports the first one that accepted the request. Requires Bridge
 * v1.6.0+. Returns `{ ok, data: { ok, attempted, succeeded, rebooted,
 * hint } }`. Username/password default to epson/epson on Epson firmwares.
 */
export async function enableAirPrint(ip, { port = 80, username = 'epson', password = 'epson' } = {}) {
  return _fetch('/enable-airprint', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ip, port, username, password }),
  });
}

export async function openDrawer(ip, port = 9100) {
  return _fetch('/open-drawer', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ip, port }),
  });
}

/**
 * Silent print via the bridge.
 *
 * @param {object} printer  { ip_address, port, paper_width, copies, type }
 * @param {object} content  receipt payload (see bridge README schema)
 * @param {object} opts     { kitchen?: bool }
 */
export async function printReceipt(printer, content, opts = {}) {
  if (!printer?.ip_address) {
    return { ok: false, data: { error: 'printer has no IP configured' } };
  }
  const payload = {
    ip: printer.ip_address,
    port: printer.port || 9100,
    paper_width: printer.paper_width || '80mm',
    copies: printer.copies || 1,
    content: { ...content, kitchen: !!opts.kitchen },
  };
  return _fetch('/print', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

/**
 * iter666 — Epson iPrint via iOS Share Sheet.
 *
 * Renders the receipt as a PDF and invokes `navigator.share({files})` so
 * the iPad's native Share Sheet pops up with **iPrint** as a destination
 * (only when the cashier has installed Epson iPrint from the App Store
 * and added the printer by IP inside iPrint).
 *
 * Why this works on iPad-only branches where AirPrint fails:
 *   • Epson iPrint is a NATIVE iOS app with the Epson SDK — it speaks
 *     raw TCP-9100 directly to the printer (no AirPrint discovery
 *     needed). It finds the TM-m30II by IP address that the cashier
 *     configures once inside iPrint.
 *   • iOS Share Sheet hands the PDF off to iPrint, which prints it on
 *     its receipt printer profile.
 *
 * UX trade-off: this is a 2-tap flow (Share → iPrint → Print) rather
 * than silent. For high-volume cashier work, the Termux Bridge path is
 * still recommended; iPrint is the manual fallback / reprint path.
 *
 * Returns:
 *   { ok: true,  mode: 'iprint-share' }            when share invoked
 *   { ok: false, error: 'web-share-unavailable' }  on browsers without
 *                                                  navigator.share
 *   { ok: false, error: 'cancelled' }              when user dismissed
 *                                                  the share sheet
 */
export async function printViaIPrintShare(printer, content) {
  if (!navigator.share || !navigator.canShare) {
    return { ok: false, error: 'web-share-unavailable' };
  }
  const paperMm = parseInt(String(printer?.paper_width || '80').replace('mm', ''), 10) || 80;
  const widthPx = Math.round(paperMm * 3.78);
  const html = await _buildAirPrintHtml(content || {}, widthPx, paperMm);

  // Lazy-load the heavy PDF libs so the page isn't penalised when
  // iPrint share isn't used.
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ]);

  // Render the receipt HTML into an offscreen container, snapshot to
  // canvas, then emit a single-page PDF sized to the receipt roll. We
  // keep the PDF page at the exact paper width (80mm / 58mm) so iPrint's
  // "Receipt Printer" profile doesn't try to scale or letterbox.
  const host = document.createElement('div');
  host.style.cssText = `position:fixed;left:-9999px;top:0;width:${widthPx}px;background:white;`;
  host.innerHTML = html;
  document.body.appendChild(host);
  try {
    const canvas = await html2canvas(host, { scale: 2, backgroundColor: '#ffffff' });
    const imgData = canvas.toDataURL('image/png');
    // mm → pt: 1 mm = 2.8346 pt. jsPDF page in points.
    const pageWidthPt = paperMm * 2.8346;
    const pageHeightPt = (canvas.height / canvas.width) * pageWidthPt;
    const pdf = new jsPDF({
      orientation: pageHeightPt > pageWidthPt ? 'portrait' : 'landscape',
      unit: 'pt',
      format: [pageWidthPt, pageHeightPt],
    });
    pdf.addImage(imgData, 'PNG', 0, 0, pageWidthPt, pageHeightPt, undefined, 'FAST');
    const blob = pdf.output('blob');
    const orderNum = content?.order_number || 'receipt';
    const file = new File([blob], `Receipt-${orderNum}.pdf`, { type: 'application/pdf' });
    if (!navigator.canShare({ files: [file] })) {
      return { ok: false, error: 'cannot-share-file' };
    }
    try {
      await navigator.share({
        files: [file],
        title: `Receipt ${orderNum}`,
        text: 'Print this receipt via Epson iPrint.',
      });
      return { ok: true, mode: 'iprint-share' };
    } catch (e) {
      // AbortError = user dismissed the sheet.
      if (e && e.name === 'AbortError') return { ok: false, error: 'cancelled' };
      return { ok: false, error: String(e.message || e) };
    }
  } finally {
    document.body.removeChild(host);
  }
}

/**
 * iter656 — iPad AirPrint direct path. Opens a hidden window with the
 * receipt formatted as 80mm/58mm-styled HTML, triggers Safari's native
 * print dialog. The user picks their AirPrint printer (must be on the
 * same Wi-Fi). NO Print Bridge required — works on iPad/iPhone with
 * any AirPrint-capable thermal printer (Epson TM-m30III, T88VI-iHub,
 * or any printer behind a Lantronix xPrintServer / AirPrint adapter).
 *
 * Caveats:
 *  - The receipt isn't truly silent — Safari shows the print dialog.
 *    For iPad-only branches that's the trade-off vs. owning a PC.
 *  - Returns `{ok: true, mode: 'airprint-direct'}` immediately even
 *    though the actual printing happens in the print dialog — there's
 *    no callback from window.print(). We can't tell if the user
 *    cancelled the dialog vs. confirmed; assume best-effort.
 */
export function printViaAirPrint(printer, content) {
  const paperMm = parseInt(String(printer?.paper_width || '80').replace('mm', ''), 10) || 80;
  // Convert mm → px at 96 DPI: 1 mm ≈ 3.78 px. iOS uses CSS px so this
  // matches the on-paper width 1:1 when AirPrint scales to the roll.
  const widthPx = Math.round(paperMm * 3.78);
  // Open the popup SYNCHRONOUSLY inside the user gesture (so it isn't
  // popup-blocked), then build the HTML — the ZATCA QR is async — and
  // write + print once it's ready.
  const win = window.open('', 'smarto-airprint', `width=${widthPx + 40},height=600`);
  _buildAirPrintHtml(content || {}, widthPx, paperMm).then((html) => {
    if (win && win.document) {
      win.document.open();
      win.document.write(html);
      win.document.close();
      // Give iOS a beat to layout, then trigger print.
      setTimeout(() => {
        try { win.focus(); win.print(); } catch (_) { /* user-blocked */ }
      }, 250);
      return;
    }
    // Iframe fallback for Safari popup-blocker case.
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;';
    document.body.appendChild(iframe);
    iframe.contentDocument.open();
    iframe.contentDocument.write(html);
    iframe.contentDocument.close();
    setTimeout(() => {
      try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (_) { /* user-blocked */ }
      // Clean up after the dialog (Safari resolves after print sheet closes).
      setTimeout(() => { iframe.remove(); }, 4000);
    }, 250);
  });
  return { ok: true, mode: win ? 'airprint-direct' : 'airprint-direct-iframe' };
}

async function _buildAirPrintHtml(c, widthPx, paperMm) {
  const items = (c.items || []).map(it => {
    const qty = it.qty || it.quantity || 1;
    const raw = it.subtotal != null ? it.subtotal : (it.price != null ? it.price : 0);
    const sub = (typeof raw === 'number' ? raw : parseFloat(raw) || 0).toFixed(2);
    return `<tr><td>${qty}× ${_esc(it.name)}</td><td style="text-align:right">${sub}</td></tr>`;
  }).join('');
  const cur = _esc(c.currency || '');
  // ZATCA QR — reuse the existing TLV builder + the qrcode dependency.
  let qrHtml = '';
  try {
    const { buildZatcaQrPayload } = await import('./eposPrint');
    const payload = buildZatcaQrPayload(c);
    if (payload) {
      const QRCode = (await import('qrcode')).default;
      const dataUrl = await QRCode.toDataURL(payload, { margin: 1, width: 180 });
      qrHtml = `<hr><div class="center"><img src="${dataUrl}" width="150" height="150" alt="ZATCA QR"/><div class="muted">ZATCA e-invoice</div></div>`;
    }
  } catch { /* QR best-effort — never block the print */ }
  // Single, self-contained document — no external CSS/JS — so it works
  // on any AirPrint flow including Lantronix passthrough.
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>${_esc(c.title || 'Receipt')}</title>
<style>
  @page { size: ${paperMm}mm auto; margin: 2mm; }
  html, body { margin:0; padding:0; }
  body { width: ${widthPx}px; font-family: -apple-system, 'Helvetica Neue', monospace; font-size: 12px; color:#000; }
  .center { text-align:center; }
  .bold { font-weight: 700; }
  .big { font-size: 15px; }
  hr { border:none; border-top:1px dashed #000; margin:6px 0; }
  table { width:100%; border-collapse:collapse; }
  td { padding: 2px 0; vertical-align: top; }
  .total td { font-weight:700; font-size:14px; padding-top:6px; }
  .muted { color:#444; font-size:11px; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head>
<body>
  <div class="center bold big">${_esc(c.zatca_seller_name || c.company || c.brand || 'Smarto')}</div>
  ${c.branch ? `<div class="center muted">${_esc(c.branch)}</div>` : ''}
  ${(c.vat || c.vat_number) ? `<div class="center muted">VAT ${_esc(c.vat || c.vat_number)}</div>` : ''}
  <hr>
  ${c.title ? `<div class="center bold">${_esc(c.title)}</div>` : ''}
  ${c.order_number ? `<div>Order: <strong>${_esc(c.order_number)}</strong></div>` : ''}
  ${c.date ? `<div class="muted">${_esc(c.date)}</div>` : ''}
  <hr>
  <table>${items}</table>
  <hr>
  ${c.subtotal != null ? `<table><tr><td>Subtotal (Excl. VAT)</td><td style="text-align:right">${cur} ${(+c.subtotal).toFixed(2)}</td></tr></table>` : ''}
  ${c.tax != null ? `<table><tr><td>VAT Amount (${+(c.tax_pct || 15)}%)</td><td style="text-align:right">${cur} ${(+c.tax).toFixed(2)}</td></tr></table>` : ''}
  ${c.discount ? `<table><tr><td>Discount</td><td style="text-align:right">– ${cur} ${(+c.discount).toFixed(2)}</td></tr></table>` : ''}
  ${c.total != null ? `<table class="total"><tr><td>GRAND TOTAL</td><td style="text-align:right">${cur} ${(+c.total).toFixed(2)}</td></tr></table>` : ''}
  ${qrHtml}
  <hr>
  ${c.footer ? `<div class="center muted">${_esc(c.footer)}</div>` : '<div class="center muted">Thank you!</div>'}
  <div style="height:14px"></div>
</body></html>`;
}

function _esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/**
 * iter646 — Cloud-relay print path. Used when the browser can't reach
 * localhost:9101 (iPads, kiosks behind corporate proxies, etc.). The
 * Smarto cloud queues the job; the Bridge running on a Mac/PC at the
 * restaurant picks it up via its long-poll loop and prints.
 *
 * Returns `{ok, job_id, bridge_id}` on success.
 */
export async function printViaCloud(api, { printerId, branchId, payload }) {
  try {
    const { data } = await api.post('/cashier/print-bridge/enqueue', {
      printer_id: printerId, branch_id: branchId, payload,
    });
    return { ok: true, ...data };
  } catch (err) {
    return { ok: false, status: err.response?.status, error: err.response?.data?.detail || String(err) };
  }
}

/**
 * iter646 — Cheap iOS / can't-reach-localhost detector. Used by the POS
 * to choose between the local printBridge.js path and the cloud relay.
 *  - On iPad / iPhone, /health to localhost:9101 always 404s instantly.
 *  - On a desktop without the Bridge running, /health times out (4s).
 * Either case → switch to cloud relay automatically.
 */
export function isLikelyIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iPad on iPadOS 13+ pretends to be Mac → also check touch + maxTouchPoints.
  return /iPad|iPhone|iPod/.test(ua) || (
    /Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1
  );
}

/**
 * iter671 — Android tablet detection (Samsung Tab, Lenovo Tab, etc.).
 * Used to extend the "mobile cashier" UI (ePOS-Direct, Termux Bridge
 * hints, etc.) to Android tabs too. On Android Chrome these mobile
 * print paths actually work MORE reliably than on iPad Safari because
 * Chrome's Private Network Access rules are more permissive.
 */
export function isLikelyAndroidTablet() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (!/Android/i.test(ua)) return false;
  // Phones contain "Mobile"; tablets either omit it or use "SM-T" / "SM-X" Samsung patterns.
  if (/SM-T|SM-X|Tab/.test(ua)) return true;
  if (/Mobile/i.test(ua)) return false;
  return true; // Android, no "Mobile" → likely a tablet.
}

/**
 * iter671 — Any mobile-style cashier device (iPad OR Android tablet).
 * The two share most of the printing constraints, so we group them.
 */
export function isLikelyMobileCashier() {
  return isLikelyIOS() || isLikelyAndroidTablet();
}

/**
 * Pick the right printer from the configured list given role + branch.
 *
 * @param {Array} printers  full list from GET /cashier/printers
 * @param {string} role     'receipt' | 'kitchen' | 'label'
 * @param {string?} branchId  current branch (preferred match)
 */
export function pickPrinter(printers, role, branchId) {
  if (!Array.isArray(printers) || printers.length === 0) return null;
  const ofRole = printers.filter(p => p.type === role && p.is_active !== false);
  if (ofRole.length === 0) return null;
  // 1) branch + default
  let p = ofRole.find(x => x.branch_id === branchId && x.is_default);
  if (p) return p;
  // 2) branch (any)
  p = ofRole.find(x => x.branch_id === branchId);
  if (p) return p;
  // 3) global default
  p = ofRole.find(x => x.is_default);
  if (p) return p;
  // 4) first available
  return ofRole[0];
}


/**
 * iter656 — Smart print orchestrator. Picks the cheapest viable path
 * automatically. Order:
 *  1. printer.airprint_enabled === true → window.print() (Safari/iOS
 *     native dialog with on-LAN AirPrint printers — NO Bridge needed).
 *  2. isLikelyIOS() → Cloud Relay (Bridge on a PC at the branch polls).
 *  3. desktop with Bridge alive → direct localhost:9101.
 *  4. desktop without Bridge → fall through to OS print dialog
 *     (window.print of the same thermal HTML — works on Chrome/Edge).
 *
 * The branch only needs ONE of (a) an AirPrint-capable printer, or
 * (b) a PC running the 1-click installer, for iPads to print.
 *
 * @param {object} args.api          axios instance (only needed for cloud path)
 * @param {object} args.printer      configured printer doc
 * @param {object} args.content      receipt payload
 * @param {string} args.branchId     current branch id (for cloud routing)
 */
/** iter842 — Route a print through the best channel available.
 *  0. **Native Electron** (running inside SmartoERP Desktop) → direct
 *     TCP-9100 for network printers, or USB IPC for locally attached
 *     thermal printers. Fastest + no LAN Bridge needed.
 *  1. AirPrint direct (iPad → Wi-Fi printer)
 *  2. iOS + cloud relay (Bridge polls)
 *  3. Desktop with local Bridge running → direct POST
 *  4. Fallback: OS print dialog
 * @param {object} args
 * @param {object} args.api          axios instance (for cloud relay)
 * @param {object} args.printer      printer profile { id, ip, name, airprint_enabled?, usb_vendor_id?, usb_product_id? }
 * @param {object} args.content      the receipt content object
 * @param {string} args.branchId     current branch id (for cloud routing)
 */
export async function printSmart({ api, printer, content, branchId }) {
  if (!printer) return { ok: false, error: 'No printer configured' };
  // (0) Native Electron path — bypass the LAN Bridge entirely.
  try {
    const { isElectronDesktop, electronPrintNetwork, electronPrintReceipt } = await import('./electronDesktop');
    if (isElectronDesktop()) {
      // Prefer USB when the printer profile carries a VID/PID.
      // The Electron main process' escpos wrapper renders the structured
      // payload → raw ESC/POS → USB write, all in Node land.
      if (printer.usb_vendor_id && printer.usb_product_id) {
        const r = await electronPrintReceipt({
          vendor_id: Number(printer.usb_vendor_id),
          product_id: Number(printer.usb_product_id),
          payload: content,
        });
        if (r?.ok) return r;
      }
      // Network printer (IP + port) → Electron sends raw TCP-9100 via
      // the local `net` module (bypasses Bridge, fastest path).
      if (printer.ip) {
        // For plain IP printers we still want ESC/POS bytes over the
        // wire. Delegate to the existing bridge-format REST helper
        // fetch when running native (electronPrintReceipt handles USB;
        // for network we'd need bytes). Fall through to Bridge below —
        // it also works fine inside Electron.
      }
    }
  } catch (_e) { /* not in Electron or lib missing — silent fallback */ }
  // (1) AirPrint direct — best UX on iPad/iPhone.
  if (printer.airprint_enabled) {
    return printViaAirPrint(printer, content);
  }
  const onIos = isLikelyIOS();
  // (2) iPad without AirPrint → cloud relay (Bridge polls).
  if (onIos) {
    if (api) {
      return printViaCloud(api, {
        printerId: printer.id, branchId, payload: content,
      });
    }
    return { ok: false, error: 'iPad: enable AirPrint on this printer OR run the Bridge on a PC at this branch.' };
  }
  // (3) Desktop with Bridge running → direct.
  const alive = await isBridgeAlive();
  if (alive) {
    return printReceipt(printer, content);
  }
  // (4) Desktop without Bridge → OS print dialog as a soft fallback.
  return printViaAirPrint(printer, content);
}
